import React, { useState } from 'react';
import { Handle, Position } from 'reactflow';
import { GitBranch, CheckCircle, XCircle, Clock, AlertTriangle, ChevronDown, ChevronUp, Route, ArrowRight } from 'lucide-react';

const SplitNode = ({ data, isConnectable, selected }) => {
  const [isResultsExpanded, setIsResultsExpanded] = useState(false);
  
  // Parse execution results if available
  let executionResult = null;
  let executionStatus = null;
  
  if (data.executionResult) {
    try {
      executionResult = typeof data.executionResult === 'string' 
        ? JSON.parse(data.executionResult) 
        : data.executionResult;
      executionStatus = data.executionStatus || 'completed';
    } catch (e) {
      console.error('Error parsing execution result:', e);
    }
  }

  const getStatusIcon = () => {
    switch (executionStatus) {
      case 'completed':
        return <CheckCircle className="h-3 w-3 text-purple-600" />;
      case 'failed':
        return <XCircle className="h-3 w-3 text-red-500" />;
      case 'running':
        return <Clock className="h-3 w-3 text-purple-500 animate-spin" />;
      default:
        return null;
    }
  };

  const getNodeBorderColor = () => {
    if (selected) {
      return 'border-purple-400 shadow-purple-200/50 shadow-lg';
    }
    
    switch (executionStatus) {
      case 'completed':
        return 'border-purple-300 shadow-purple-100/50';
      case 'failed':
        return 'border-red-300 shadow-red-100/50';
      case 'running':
        return 'border-purple-300 shadow-purple-100/50';
      default:
        return 'border-purple-200 shadow-purple-50/50';
    }
  };

  const getBranchColor = (branchId) => {
    const colors = {
      'branch-1': '#10B981', // emerald
      'branch-2': '#EF4444', // red
      'branch-3': '#F59E0B', // amber
      'branch-4': '#8B5CF6', // violet
      'branch-5': '#06B6D4', // cyan
    };
    return colors[branchId] || '#6B7280'; // default gray
  };

  const branches = data.branches || [
    { id: 'branch-1', name: 'Branch A', condition: null },
    { id: 'branch-2', name: 'Branch B', condition: null }
  ];

  const activeBranches = executionResult?.activeBranches || [];
  const splitDecision = executionResult?.splitDecision;

  return (
    <div className={`
      bg-white border-2 rounded-2xl shadow-soft min-w-[280px] max-w-[350px] 
      transition-all duration-200 hover:shadow-medium relative
      ${getNodeBorderColor()}
    `}>
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        style={{ 
          background: '#a855f7',
          width: '14px',
          height: '14px',
          border: '2px solid #7c3aed',
          borderRadius: '50%'
        }}
        isConnectable={isConnectable}
      />
      
      {/* Output Handles - One for each branch */}
      {branches.map((branch, index) => (
        <Handle
          key={branch.id}
          type="source"
          position={Position.Right}
          id={branch.id}
          style={{ 
            background: getBranchColor(branch.id),
            width: '14px',
            height: '14px',
            border: `2px solid ${getBranchColor(branch.id)}`,
            borderRadius: '50%',
            top: `${30 + (index * 25)}%`
          }}
          isConnectable={isConnectable}
        />
      ))}
      
      {/* Node Header */}
      <div className="bg-gradient-to-r from-purple-100 to-purple-50 px-4 py-3 rounded-t-2xl border-b border-purple-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-1.5 bg-purple-200 rounded-lg">
              <GitBranch className="h-4 w-4 text-purple-800" />
            </div>
            <div>
              <span className="text-sm font-semibold text-purple-900">
                {data.label || 'Split Node'}
              </span>
              <div className="text-xs text-purple-700 opacity-75">
                Conditional Branching
              </div>
            </div>
          </div>
          {executionStatus && (
            <div className="flex items-center">
              {getStatusIcon()}
            </div>
          )}
        </div>
      </div>
      
      {/* Node Content */}
      <div className="p-4 space-y-3">
        {/* Configuration Display */}
        <div className="space-y-3">
          {/* Split Type */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-700">Type:</span>
            <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
              {data.splitType === 'parallel' ? '⚡ Parallel' : 
               data.splitType === 'percentage' ? '📊 Percentage' : 
               '🔀 Conditional'}
            </span>
          </div>
          
          {/* Branches */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Route className="h-3 w-3 text-gray-500" />
              <span className="text-xs font-medium text-gray-700">Branches ({branches.length}):</span>
            </div>
            
            {branches.map((branch, index) => {
              const isActive = activeBranches.includes(branch.id);
              const branchDecision = splitDecision?.evaluatedConditions?.find(
                cond => cond.branchId === branch.id
              );
              
              return (
                <div 
                  key={branch.id} 
                  className={`
                    flex items-center justify-between p-2 rounded-lg border transition-all
                    ${isActive 
                      ? 'bg-purple-50 border-purple-200' 
                      : executionStatus === 'completed'
                      ? 'bg-gray-50 border-gray-200'
                      : 'bg-gray-50 border-gray-200'
                    }
                  `}
                >
                  <div className="flex items-center space-x-2">
                    <div 
                      className="w-3 h-3 rounded-full border-2"
                      style={{ 
                        backgroundColor: isActive ? getBranchColor(branch.id) : 'transparent',
                        borderColor: getBranchColor(branch.id)
                      }}
                    />
                    <span className="text-xs font-medium text-gray-800">
                      {branch.name}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    {executionStatus === 'completed' && (
                      isActive ? (
                        <CheckCircle className="h-3 w-3 text-purple-600" />
                      ) : (
                        <div className="w-3 h-3 rounded-full bg-gray-300" />
                      )
                    )}
                    
                    {branch.condition && (
                      <span className="text-xs text-gray-500 truncate max-w-16">
                        {branch.condition.field} {branch.condition.operator} {branch.condition.value}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Default Branch */}
          {data.defaultBranch && (
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-700">Default:</span>
              <span className="text-xs text-gray-600">
                {branches.find(b => b.id === data.defaultBranch)?.name || 'None'}
              </span>
            </div>
          )}
          
          {/* Aggregation Mode */}
          {data.aggregationMode && (
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-700">Aggregation:</span>
              <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                {data.aggregationMode === 'all' ? 'Wait All' :
                 data.aggregationMode === 'first' ? 'First Complete' :
                 data.aggregationMode === 'majority' ? 'Majority' :
                 'Custom'}
              </span>
            </div>
          )}
        </div>

        {/* Execution Results Summary */}
        {executionResult && (
          <div className="pt-3 border-t border-gray-200">
            <button
              onClick={() => setIsResultsExpanded(!isResultsExpanded)}
              className="w-full flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <div className="flex items-center space-x-2">
                <ArrowRight className="h-3 w-3 text-purple-600" />
                <span className="text-xs font-medium text-gray-800">Split Results</span>
              </div>
              {isResultsExpanded ? (
                <ChevronUp className="h-3 w-3 text-gray-500" />
              ) : (
                <ChevronDown className="h-3 w-3 text-gray-500" />
              )}
            </button>
            
            {isResultsExpanded && (
              <div className="mt-2 space-y-2">
                {/* Active Branches Summary */}
                <div className="text-xs text-gray-600">
                  <div className="flex items-center justify-between mb-1">
                    <span>Active Branches:</span>
                    <span className="font-medium">{activeBranches.length}/{branches.length}</span>
                  </div>
                  
                  {/* Branch Decisions */}
                  {splitDecision?.evaluatedConditions && (
                    <div className="space-y-1 mt-2">
                      {splitDecision.evaluatedConditions.map((condition, index) => (
                        <div 
                          key={index}
                          className={`text-xs p-2 rounded border ${
                            condition.matched 
                              ? 'bg-purple-50 border-purple-200 text-purple-800'
                              : 'bg-gray-50 border-gray-200 text-gray-600'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">
                              {branches.find(b => b.id === condition.branchId)?.name}
                            </span>
                            <span className={condition.matched ? 'text-purple-600' : 'text-gray-500'}>
                              {condition.matched ? '✓' : '✗'}
                            </span>
                          </div>
                          {condition.condition && (
                            <div className="text-xs opacity-75 mt-1">
                              {condition.condition.field} {condition.condition.operator} "{condition.condition.value}"
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SplitNode; 